/**
 * Theme registry — the catalogue the appearance picker renders from, and the
 * allow-list the theme store validates against.
 *
 * A theme is a CSS file under `src/themes/` that redefines the whole token
 * contract (see README.md) under `html[data-theme='<id>']`, plus one entry
 * here. Nothing else in the app knows a theme exists: components read tokens,
 * never theme names.
 *
 * `preview` is deliberately duplicated data. The catalogue has to draw every
 * theme's swatches at once, and custom properties only ever resolve for the
 * *active* theme, so the picker cannot read them out of the stylesheet. It is
 * the only place outside `themes/*.css` where a literal colour is allowed, and
 * it carries one palette per colour mode so a preview shows what the viewer
 * would actually get. It also feeds the `theme-color` meta, which is why the
 * `background` values must stay equal to each theme's `--color-bg-dark`.
 */

/** Theme applied when nothing is stored, and fallback for an unknown id. */
export const DEFAULT_THEME_ID = 'inwd';

/** Colour modes every theme must implement. */
export const COLOR_MODES = ['dark', 'light'];

export const THEMES = [
  {
    id: 'inwd',
    // Which glyphs `components/Icon.svelte` draws (see scripts/generate-icons.mjs).
    iconSet: 'iconoir',
    // Capability: this theme has optional illustrated details. Components
    // read capabilities, never theme ids; Appearance shows the switch only
    // when the active theme has `details`, and PageLoader reads `loader`.
    details: {
      key: 'inwdDetails',
      loader: 'forest',
      labelKey: 'appearance.details.label',
      descriptionKey: 'appearance.details.description',
      // The swatch beside the switch: the navigation frame cut from the atlas
      // with the same coordinates inwd.css uses. Duplicated for the same
      // reason as `preview` — the switch shows it while the details are off,
      // when no token carries it.
      preview: {
        image: '/inwd/ui-containers.webp',
        size: '179.649% 341.333%',
        position: '6.461% 5.801%',
      },
    },
    labelKey: 'appearance.themes.inwd.label',
    taglineKey: 'appearance.themes.inwd.tagline',
    descriptionKey: 'appearance.themes.inwd.description',
    preview: {
      dark: {
        background: '#0f1c28',
        surface: '#172839',
        text: '#c9d4dc',
        primary: '#e6edf2',
        secondary: '#e8958d',
      },
      light: {
        background: '#ffffff',
        surface: '#f7f9f8',
        text: '#2f4657',
        primary: '#172d40',
        secondary: '#934844',
      },
      headingFont: "'Cormorant Garamond', Georgia, serif",
      bodyFont: "'IBM Plex Sans', system-ui, sans-serif",
      radius: '3px',
    },
  },
  {
    id: 'webdeploy',
    iconSet: 'pixel',
    // i18n keys; the UI never hardcodes a theme name or description.
    labelKey: 'appearance.themes.webdeploy.label',
    taglineKey: 'appearance.themes.webdeploy.tagline',
    descriptionKey: 'appearance.themes.webdeploy.description',
    preview: {
      dark: {
        background: '#050a10',
        surface: '#0e1621',
        text: '#b0b8c4',
        primary: '#00f0ff',
        secondary: '#ff00ff',
      },
      light: {
        background: '#ffffff',
        surface: '#f9fafb',
        text: '#374151',
        primary: '#00c0cc',
        secondary: '#cc00cc',
      },
      headingFont: "'VT323', monospace",
      bodyFont: "'Space Mono', monospace",
      radius: '0px',
    },
  },
];

const BY_ID = new Map(THEMES.map(theme => [theme.id, theme]));

/** True when `id` names a theme that is actually shipped. */
export function isKnownThemeId(id) {
  return BY_ID.has(id);
}

/** The theme record for `id`, or the default one when `id` is unknown. */
export function getTheme(id) {
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_THEME_ID);
}

/** True when `mode` is one of the two colour modes every theme implements. */
export function isKnownColorMode(mode) {
  return COLOR_MODES.includes(mode);
}
